/**
 * "The user has a screen in front of them" — the barrier lazy hydration's hot
 * set waits on before it starts preloading.
 *
 * Stage 1.5 in `internal/store/flashka-store.ts` reads up to 18 decks' card
 * bodies off disk so the next deck the user taps is already resident. That work
 * has always been DOCUMENTED as running "after first paint", but nothing
 * enforced it: it was kicked off the moment stage 1 finished, and on a cold
 * start stage 1 finishing and the navigator mounting are the same two seconds.
 * Preloading is speculative. The screen the user is waiting for is not, and it
 * should not have to share the JS thread with eighteen SQLite reads and
 * eighteen store writes to win.
 *
 * ONE-WAY on purpose: once the app has painted it never goes back to
 * not-painted, so a later hydrate (a user switch) preloads immediately instead
 * of waiting for a paint that already happened.
 *
 * The timeout in {@link whenAppReady} is part of the contract, not a safety net
 * to be trimmed away: a surface that never signals — a deep link straight into
 * a deck, a headless test, any client that does not opt into lazy card
 * hydration at all — must LOSE NOTHING, only wait a beat longer for work it was
 * never going to notice.
 */

let painted = false;
const waiters = new Set<() => void>();

/** Called once from the first screen that commits (see `HomeScreen`). */
export function signalAppReady(): void {
  if (painted) return;
  painted = true;
  // Copied before iterating: a waiter removes itself from the set as it runs.
  for (const wake of [...waiters]) wake();
  waiters.clear();
}

export function isAppReady(): boolean {
  return painted;
}

/**
 * Resolves when the app has painted, or after `timeoutMs`, whichever is first.
 * Never rejects — a caller uses this to DELAY work, not to decide whether to
 * do it.
 */
export function whenAppReady(timeoutMs: number): Promise<void> {
  if (painted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      waiters.delete(finish);
      clearTimeout(timer);
      resolve();
    };
    waiters.add(finish);
    // Declared after `finish` closes over it — the closure only runs later, and
    // an unsettled waiter must not leave a timer holding the event loop open.
    const timer = setTimeout(finish, timeoutMs);
  });
}

/** Tests only — forget that the app painted. */
export function resetAppReadyForTests(): void {
  painted = false;
  waiters.clear();
}
